import React, { useState, useEffect, useCallback, useRef } from "react";
import api from "./api";
import { FaFileCsv, FaFileAlt, FaSave } from "react-icons/fa";
import { DateTime } from "luxon";

const ENDPOINT = "/api/payrollDnevnikFun";

// These exports are normally windows-1250, and the company name is the only
// place it shows. Read as UTF-8 first and fall back when that produces the
// replacement character, so a correct UTF-8 export still works.
const readCsv = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    const fail = () => reject(new Error(`Ne mogu da pročitam fajl ${file.name}.`));
    reader.onerror = fail;

    // Without TextDecoder there is nothing to re-decode with, so read text
    // directly. Anything thrown in onload would otherwise leave this promise
    // unsettled and the page waiting forever.
    if (typeof TextDecoder === "undefined") {
      reader.onload = () => resolve(String(reader.result));
      reader.readAsText(file);
      return;
    }

    reader.onload = () => {
      try {
        const buffer = reader.result;
        const utf8 = new TextDecoder("utf-8").decode(buffer);
        if (!utf8.includes("�")) {
          resolve(utf8);
          return;
        }
        try {
          resolve(new TextDecoder("windows-1250").decode(buffer));
        } catch (e) {
          resolve(utf8); // decoder unavailable; mangled beats nothing
        }
      } catch (e) {
        fail();
      }
    };
    reader.readAsArrayBuffer(file);
  });

// The backend wants the journal as base64; readAsDataURL is the only FileReader
// mode that gives it, so strip the "data:...;base64," prefix.
const readAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
    reader.onerror = () => reject(new Error(`Ne mogu da pročitam fajl ${file.name}.`));
    reader.readAsDataURL(file);
  });

const blobFromBase64 = (base64) => {
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  return new Blob([bytes], { type: "application/dbf" });
};

// Amounts arrive as exact strings such as "621461.88". Reformat for display
// only, never recompute: the backend has already done the arithmetic.
const formatAmount = (value) => {
  const [whole, decimals = "00"] = String(value === undefined || value === null ? "0" : value).split(".");
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${grouped},${decimals}`;
};

// Seven of the nine rows are zero on one side. Blanking them is how a ledger is
// printed and it makes the two columns legible at a glance.
const amountCell = (value) => (Number(value) === 0 ? "" : formatAmount(value));

// The backend sends the posting date as GGGGMMDD.
const formatDatum = (yyyymmdd) => {
  const parsed = DateTime.fromFormat(String(yyyymmdd || ""), "yyyyMMdd");
  return parsed.isValid ? parsed.toFormat("dd.MM.yyyy") : String(yyyymmdd || "");
};

// PLATE_06.dbf for June. Used for the button label and the suggested name; the
// backend's own filename wins once the response arrives.
const plateFileName = (obracunskiPeriod) => {
  const mesec = String(obracunskiPeriod || "").split("-")[1] || "00";
  return `PLATE_${mesec}.dbf`;
};

const godina = (obracunskiPeriod) =>
  String(obracunskiPeriod || "").split("-")[0] || "";

const fileNameFromHeaders = (headers, fallback) => {
  const match = /filename=([^;]+)/i.exec(headers["content-disposition"] || "");
  return match ? match[1].trim().replace(/"/g, "") : fallback;
};

// A 422 is an expected outcome here, not a crash: the backend refuses a CSV it
// cannot map, or a DBF that is not a journal, and its message names the exact
// problem. Show it verbatim, since it is the most useful thing on the screen.
const extractMessage = async (error) => {
  const status = error.response?.status;
  const data = error.response?.data;

  // With responseType blob (the single-file save) the error body is a Blob;
  // otherwise axios has already parsed it.
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      if (parsed.message) return parsed.message;
    } catch (e) {
      /* fall through to the generic message */
    }
  } else if (data && typeof data === "object" && data.message) {
    return data.message;
  }

  return !status || status >= 500
    ? "Greška na serveru ili vezi. Pokušajte ponovo za par sekundi."
    : "Konverzija nije uspela.";
};

const triggerDownload = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

function ZaradePage() {
  const [csvFile, setCsvFile] = useState(null);
  const [dbfFile, setDbfFile] = useState(null);
  const [dragging, setDragging] = useState(null); // "csv" | "dbf" | null

  const [showOverrides, setShowOverrides] = useState(false);
  const [brNalog, setBrNalog] = useState("");
  const [sadrzaj, setSadrzaj] = useState("");
  const [datum, setDatum] = useState("");

  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedFiles, setSavedFiles] = useState(null);
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);

  const csvInputRef = useRef(null);
  const dbfInputRef = useRef(null);

  const overridesValid = !datum || /^\d{4}-\d{2}-\d{2}$/.test(datum);

  const overrideParams = useCallback(() => {
    const params = new URLSearchParams();
    if (brNalog) params.set("brNalog", brNalog);
    if (sadrzaj) params.set("sadrzaj", sadrzaj);
    if (datum) params.set("datum", datum.replace(/-/g, "")); // backend wants GGGGMMDD
    return params;
  }, [brNalog, sadrzaj, datum]);

  // The nalog is the same whether or not a journal is being updated, so the
  // preview never sends the journal file. Ticking the checkbox or picking a
  // journal therefore does not need a new preview.
  useEffect(() => {
    setPreview(null);
    setMessage("");
    setIsError(false);
    setSavedFiles(null);

    if (!csvFile || !overridesValid) return undefined;

    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const params = overrideParams();
        params.set("preview", "1");
        const body = await readCsv(csvFile);
        const response = await api.post(`${ENDPOINT}?${params.toString()}`, body, {
          headers: { "Content-Type": "text/csv" },
        });
        if (cancelled) return;
        setPreview(response.data);
      } catch (error) {
        if (cancelled) return;
        console.error("Knjiženje zarada preview error:", error);
        setIsError(true);
        setMessage(await extractMessage(error));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [csvFile, overridesValid, overrideParams]);

  const acceptCsv = (file) => {
    if (!file) return;
    if (!/\.csv$/i.test(file.name)) {
      setIsError(true);
      setMessage("Izaberite CSV fajl koji izvozi program za zarade.");
      return;
    }
    setCsvFile(file);
  };

  const acceptDbf = (file) => {
    if (!file) return;
    if (!/\.dbf$/i.test(file.name)) {
      setIsError(true);
      setMessage("Izaberite dnevnik u DBF formatu (dnevnik.dbf).");
      return;
    }
    setMessage("");
    setIsError(false);
    setDbfFile(file);
  };

  const reset = () => {
    setCsvFile(null);
    setDbfFile(null);
    setPreview(null);
    setMessage("");
    setIsError(false);
    setSavedFiles(null);
    if (csvInputRef.current) csvInputRef.current.value = "";
    if (dbfInputRef.current) dbfInputRef.current.value = "";
  };

  // The backend labels the journal TDNEVNIK.DBF, but the point is to write it
  // back over the file it came from, whatever that file is called. Using the
  // uploaded name keeps a dnevnik.dbf a dnevnik.dbf instead of quietly leaving
  // a second journal beside it.
  const journalName = () => (dbfFile ? dbfFile.name : "dnevnik.dbf");

  const saveBothFiles = async (dir, result) => {
    const written = [];
    const named = [
      { ...result.dnevnik, filename: journalName() },
      result.plate,
    ];
    for (const entry of named) {
      const handle = await dir.getFileHandle(entry.filename, { create: true });
      const writable = await handle.createWritable();
      await writable.write(blobFromBase64(entry.base64));
      await writable.close();
      written.push(entry.filename);
    }
    return written;
  };

  const handleSave = async () => {
    setMessage("");
    setIsError(false);
    const suggested = plateFileName(preview.obracunskiPeriod);

    // Both pickers need the user gesture, so they have to be opened before any
    // await. One folder pick covers both files.
    let dirHandle = null;
    let fileHandle = null;
    try {
      if (dbfFile && window.showDirectoryPicker) {
        dirHandle = await window.showDirectoryPicker({ mode: "readwrite" });
      } else if (!dbfFile && window.showSaveFilePicker) {
        fileHandle = await window.showSaveFilePicker({
          suggestedName: suggested,
          types: [
            { description: "DBF", accept: { "application/dbf": [".dbf"] } },
          ],
        });
      }
    } catch (error) {
      // The bookkeeper closed the dialog. That is not a failure.
      if (error && error.name === "AbortError") return;
      throw error;
    }

    setSaving(true);
    try {
      const params = overrideParams();
      const csvText = await readCsv(csvFile);

      if (dbfFile) {
        // Two files come back as JSON, so this request must not ask for a blob.
        const existingDbfBase64 = await readAsBase64(dbfFile);
        const response = await api.post(
          `${ENDPOINT}?${params.toString()}`,
          // The JSON field is called xml for both formats; a legacy name that
          // simply carries the file text.
          { xml: csvText, existingDbfBase64 },
          { headers: { "Content-Type": "application/json" } }
        );
        const result = response.data;

        if (dirHandle) {
          setSavedFiles(await saveBothFiles(dirHandle, result));
        } else {
          // Firefox and Safari have neither picker.
          triggerDownload(blobFromBase64(result.dnevnik.base64), journalName());
          triggerDownload(blobFromBase64(result.plate.base64), result.plate.filename);
          setSavedFiles([journalName(), result.plate.filename]);
          setMessage(
            "Fajlovi su preuzeti u Preuzimanja (Downloads). Premestite ih u folder firme, preko postojećeg dnevnika."
          );
        }
        return;
      }

      const response = await api.post(`${ENDPOINT}?${params.toString()}`, csvText, {
        headers: { "Content-Type": "text/csv" },
        responseType: "blob",
      });
      const contentType = response.headers["content-type"] || "";

      // A JSON body means the backend answered with a message rather than a
      // file, so show it instead of saving something unusable.
      if (contentType.includes("application/json")) {
        const parsed = JSON.parse(await response.data.text());
        setIsError(true);
        setMessage(parsed.message || "Čuvanje nije uspelo.");
        return;
      }

      const blob = new Blob([response.data], { type: contentType });
      const fileName = fileNameFromHeaders(response.headers, suggested);

      if (fileHandle) {
        const writable = await fileHandle.createWritable();
        await writable.write(blob);
        await writable.close();
        setSavedFiles([fileHandle.name || fileName]);
      } else {
        triggerDownload(blob, fileName);
        setSavedFiles([fileName]);
        setMessage(
          "Fajl je preuzet u Preuzimanja (Downloads). Premestite ga u folder firme."
        );
      }
    } catch (error) {
      console.error("Knjiženje zarada save error:", error);
      setIsError(true);
      setMessage(await extractMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const zoneStyle = (active) => ({
    border: `2px dashed ${active ? "var(--primary-color, #4a7cf7)" : "var(--border-color, #ccc)"}`,
    borderRadius: 10,
    padding: "32px 20px",
    textAlign: "center",
    cursor: "pointer",
    background: active ? "rgba(74, 124, 247, 0.06)" : "transparent",
    transition: "border-color 120ms, background 120ms",
  });

  const dropHandlers = (kind, accept) => ({
    onDragOver: (e) => {
      e.preventDefault();
      setDragging(kind);
    },
    onDragLeave: () => setDragging(null),
    onDrop: (e) => {
      e.preventDefault();
      setDragging(null);
      accept(e.dataTransfer.files && e.dataTransfer.files[0]);
    },
  });

  const openPicker = (ref) => ref.current && ref.current.click();
  const keyOpens = (ref) => (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openPicker(ref);
    }
  };

  const saveLabel = dbfFile
    ? `Sačuvaj dnevnik i ${plateFileName(preview && preview.obracunskiPeriod)}`
    : `Sačuvaj ${plateFileName(preview && preview.obracunskiPeriod)}`;

  return (
    <div className="page-card page-card-wide">
      <h2 className="page-title">Knjiženje zarada u dnevnik</h2>

      {!csvFile ? (
        <div
          role="button"
          tabIndex={0}
          aria-label="Prevucite rekapitulaciju zarada ovde ili kliknite da izaberete"
          style={zoneStyle(dragging === "csv")}
          onClick={() => openPicker(csvInputRef)}
          onKeyDown={keyOpens(csvInputRef)}
          {...dropHandlers("csv", acceptCsv)}
        >
          <FaFileCsv size={40} style={{ opacity: 0.5, marginBottom: 12 }} />
          <div style={{ fontSize: 17, fontWeight: 600 }}>
            Prevucite rekapitulaciju zarada ovde
          </div>
          <div style={{ marginTop: 4 }}>ili kliknite da izaberete</div>
          <div style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 12 }}>
            CSV koji izvozi program za zarade
          </div>
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            padding: "10px 0",
          }}
        >
          <span>
            <FaFileCsv style={{ marginRight: 8, opacity: 0.6 }} />
            {csvFile.name}
          </span>
          <button type="button" className="btn-sm btn-outline" onClick={reset}>
            Ukloni
          </button>
        </div>
      )}

      <input
        ref={csvInputRef}
        type="file"
        accept=".csv"
        style={{ display: "none" }}
        onChange={(e) => acceptCsv(e.target.files[0])}
      />

      {/* The journal is optional: leave it out and only the plate file is
          produced. It is always on screen rather than behind a checkbox, so
          nobody has to discover that updating the journal is possible. */}
      {!dbfFile && (
        <div
          role="button"
          tabIndex={0}
          aria-label="Prevucite postojeći dnevnik.dbf ovde"
          style={{ ...zoneStyle(dragging === "dbf"), marginTop: 14 }}
          onClick={() => openPicker(dbfInputRef)}
          onKeyDown={keyOpens(dbfInputRef)}
          {...dropHandlers("dbf", acceptDbf)}
        >
          <FaFileAlt size={32} style={{ opacity: 0.5, marginBottom: 10 }} />
          <div style={{ fontWeight: 600 }}>Prevucite postojeći dnevnik.dbf ovde</div>
          <div style={{ marginTop: 4 }}>ili kliknite da izaberete</div>
          <div style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 10 }}>
            Opciono. Ako ga izaberete, dobićete i ažurirani dnevnik.
          </div>
        </div>
      )}

      {dbfFile && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            padding: "10px 0",
          }}
        >
          <span>
            <FaFileAlt style={{ marginRight: 8, opacity: 0.6 }} />
            {dbfFile.name}
          </span>
          <button
            type="button"
            className="btn-sm btn-outline"
            onClick={() => {
              setDbfFile(null);
              if (dbfInputRef.current) dbfInputRef.current.value = "";
            }}
          >
            Ukloni
          </button>
        </div>
      )}

      <input
        ref={dbfInputRef}
        type="file"
        accept=".dbf"
        style={{ display: "none" }}
        onChange={(e) => acceptDbf(e.target.files[0])}
      />

      {loading && <div className="status-message">Obrada rekapitulacije...</div>}

      {message && (
        <div className={`status-message ${isError ? "error" : "success"}`}>
          {message}
        </div>
      )}

      {savedFiles && (
        <div style={{ marginTop: 12 }}>
          <div className="status-message success">
            Sačuvano: {savedFiles.join(", ")}
          </div>
          <button type="button" className="btn-secondary" onClick={reset}>
            Novi obračun
          </button>
        </div>
      )}

      {preview && !savedFiles && (
        <div style={{ marginTop: 20 }}>
          <h3 className="page-title" style={{ fontSize: 18 }}>
            Pregled naloga
          </h3>

          <div style={{ marginBottom: 12 }}>
            <div style={{ fontWeight: 600, fontSize: 16 }}>{preview.firma}</div>
            <div style={{ color: "var(--text-muted)", marginTop: 2 }}>
              {preview.sadrzaj} {godina(preview.obracunskiPeriod)}
              {"  ·  "}Nalog {preview.brNalog}
              {"  ·  "}
              {formatDatum(preview.datum)}
            </div>
          </div>

          <div className="users-table-wrapper">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Konto</th>
                  <th>Opis</th>
                  <th style={{ textAlign: "right" }}>Duguje</th>
                  <th style={{ textAlign: "right" }}>Potražuje</th>
                  <th>Iz fajla</th>
                </tr>
              </thead>
              <tbody>
                {preview.stavke.map((stavka, i) => (
                  <tr key={`${stavka.konto}-${i}`}>
                    <td>
                      <code>{stavka.konto}</code>
                    </td>
                    <td>{stavka.opis}</td>
                    <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                      {amountCell(stavka.duguje)}
                    </td>
                    <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                      {amountCell(stavka.potrazuje)}
                    </td>
                    {/* Two different CSV rows both numbered 4532 end up on 4522
                        and 4532, so the source account is worth showing. */}
                    <td title={stavka.izvorniOpis || undefined}>
                      <code>{stavka.izvorniKonto}</code>
                    </td>
                  </tr>
                ))}
                <tr style={{ borderTop: "2px solid var(--border-color, #ccc)" }}>
                  <td colSpan={2}>
                    <strong>UKUPNO</strong>
                  </td>
                  <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                    <strong>{formatAmount(preview.sumDuguje)}</strong>
                  </td>
                  <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                    <strong>{formatAmount(preview.sumPotrazuje)}</strong>
                  </td>
                  <td />
                </tr>
              </tbody>
            </table>
          </div>

          <div className={`status-message ${preview.balanced ? "success" : "error"}`}>
            {preview.balanced ? "Nalog je u ravnoteži" : "Nalog nije u ravnoteži"}
          </div>

          {/* getFileHandle with create:true replaces the file in place. The
              backend keeps every existing record byte for byte, but the
              bookkeeper should know the file is replaced, not appended to. */}
          {dbfFile && (
            <div className="status-message">
              Dnevnik će biti zamenjen novom verzijom sa {preview.brojStavki} dodatih
              stavki.
            </div>
          )}

          {preview.balanced && (
            <>
              <button
                type="button"
                className="btn-primary"
                onClick={handleSave}
                disabled={saving}
              >
                <FaSave style={{ marginRight: 8 }} />
                {saving ? "Čuvanje..." : saveLabel}
              </button>
            </>
          )}
        </div>
      )}

      {csvFile && (
        <div className="form-group" style={{ marginTop: 20 }}>
          <button
            type="button"
            className="btn-sm btn-outline"
            onClick={() => setShowOverrides((v) => !v)}
          >
            {showOverrides ? "Sakrij dodatna podešavanja" : "Dodatna podešavanja"}
          </button>
        </div>
      )}

      {csvFile && showOverrides && (
        <div className="add-form-grid">
          <div className="form-group">
            <label htmlFor="zarade-brnalog">Broj naloga</label>
            <input
              id="zarade-brnalog"
              type="number"
              className="form-control"
              value={brNalog}
              onChange={(e) => setBrNalog(e.target.value)}
              placeholder="podrazumevano 60 + mesec"
            />
          </div>
          <div className="form-group">
            <label htmlFor="zarade-sadrzaj">Sadržaj</label>
            <input
              id="zarade-sadrzaj"
              type="text"
              className="form-control"
              value={sadrzaj}
              onChange={(e) => setSadrzaj(e.target.value)}
              placeholder="npr. Zarada jun"
            />
          </div>
          <div className="form-group">
            <label htmlFor="zarade-datum">Datum knjiženja</label>
            <input
              id="zarade-datum"
              type="date"
              className="form-control"
              value={datum}
              onChange={(e) => setDatum(e.target.value)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default ZaradePage;
